import { useState } from "react";
import { NavLink } from "react-router-dom";
import { usePermisos } from "@/lib/permisos";
import {
  BarChart3, Calculator, Receipt, Wallet2, CreditCard, BadgePercent, Users, BookOpen,
  CalendarHeart, Megaphone, QrCode, UserCog, Building2, ChevronDown, ChevronRight,
  ArrowUp, ArrowDown, Settings2, RotateCcw, CalendarClock, FileCode2, UtensilsCrossed,
  AlertTriangle, Boxes, BookOpenCheck, Landmark, BadgeCheck, DatabaseBackup,
} from "lucide-react";

export const GRUPOS_ADMIN = [
  { titulo: "Finanzas", items: [
    { to: "/admin", label: "Reportes", icon: BarChart3, tid: "nav-admin-reportes", permiso: "finanzas:reportes" },
    { to: "/admin/caja", label: "Cierre de caja", icon: Calculator, tid: "nav-admin-caja", permiso: "finanzas:reportes" },
    { to: "/admin/facturacion", label: "Facturación", icon: Receipt, tid: "nav-admin-facturacion", permiso: "facturas:crear" },
    { to: "/admin/mensualidades", label: "Mensualidad automática", icon: CalendarClock, tid: "nav-admin-mensualidades", permiso: "facturas:crear" },
    { to: "/admin/pagos-aprobar", label: "Pagos por aprobar", icon: BadgeCheck, tid: "nav-admin-pagos-aprobar", permiso: "facturas:cobrar" },
    { to: "/admin/gastos", label: "Gastos y ventas", icon: Wallet2, tid: "nav-admin-gastos", permiso: "finanzas:gastos_ventas" },
    { to: "/admin/inventario", label: "Inventario", icon: Boxes, tid: "nav-admin-inventario", permiso: "inventario:gestionar" },
    { to: "/admin/planes", label: "Modalidades de pago", icon: CreditCard, tid: "nav-admin-planes", permiso: "finanzas:descuentos_planes" },
    { to: "/admin/descuentos", label: "Descuentos", icon: BadgePercent, tid: "nav-admin-descuentos", permiso: "finanzas:descuentos_planes" },
  ] },
  { titulo: "Nómina", items: [
    { to: "/admin/nomina", label: "Nómina y ponches", icon: Landmark, tid: "nav-admin-nomina", permiso: "nomina:gestionar" },
    { to: "/admin/mis-pagos", label: "Mis pagos de nómina", icon: Wallet2, tid: "nav-admin-mis-pagos" },
  ] },
  { titulo: "Sistema", items: [
    { to: "/admin/sistema", label: "Respaldo y sistema", icon: DatabaseBackup, tid: "nav-admin-sistema", permiso: "sistema:respaldo" },
  ] },
  { titulo: "Académico", items: [    { to: "/admin/estudiantes", label: "Estudiantes", icon: Users, tid: "nav-admin-estudiantes", permiso: "estudiantes:registrar" },
    { to: "/admin/materias", label: "Materias y docentes", icon: BookOpen, tid: "nav-admin-materias", permiso: "materias:gestionar" },
    { to: "/admin/horarios", label: "Horario de clases", icon: CalendarClock, tid: "nav-admin-horarios", permiso: "horarios:gestionar" },
    { to: "/admin/alertas", label: "Alertas de rendimiento", icon: AlertTriangle, tid: "nav-admin-alertas", permiso: "estudiantes:registrar" },
    { to: "/admin/catalogo", label: "Actividades y Combos", icon: CalendarHeart, tid: "nav-admin-catalogo", permiso: "actividades:gestionar" },
  ] },
  { titulo: "Comunicación", items: [
    { to: "/admin/anuncios", label: "Anuncios", icon: Megaphone, tid: "nav-admin-anuncios", permiso: "comunicacion:anuncios" },
    { to: "/admin/menu", label: "Menú escolar", icon: UtensilsCrossed, tid: "nav-admin-menu", permiso: "comunicacion:menu" },
    { to: "/admin/registro", label: "Registro QR", icon: QrCode, tid: "nav-admin-registro", permiso: "registro:solicitudes" },
  ] },
  { titulo: "Configuración", items: [
    { to: "/admin/configuracion", label: "Datos del colegio", icon: Building2, tid: "nav-admin-configuracion", permiso: "configuracion:gestionar" },
    { to: "/admin/ecf", label: "Facturación electrónica", icon: FileCode2, tid: "nav-admin-ecf", permiso: "ecf:gestionar" },
    { to: "/admin/usuarios", label: "Usuarios y roles", icon: UserCog, tid: "nav-admin-usuarios", permisos: ["usuarios:gestionar", "usuarios:familias"] },
    { to: "/admin/manual", label: "Manual de uso", icon: BookOpenCheck, tid: "nav-admin-manual" },
  ] },
];

const leer = (clave, porDefecto) => {
  try { return JSON.parse(localStorage.getItem(clave)) ?? porDefecto; } catch { return porDefecto; }
};

export const AdminNav = ({ onNavigate }) => {
  const [orden, setOrden] = useState(() => leer("sis_nav_orden", {}));
  const [cerrados, setCerrados] = useState(() => leer("sis_nav_cerrados", []));
  const [editando, setEditando] = useState(false);
  const { puede } = usePermisos();

  const visible = (i) => {
    const claves = i.permisos || [i.permiso].filter(Boolean);
    return claves.length === 0 || puede(...claves);
  };

  const itemsDe = (g) => {
    const guardado = orden[g.titulo] || [];
    const disponibles = g.items.filter(visible);
    const porRuta = new Map(disponibles.map((i) => [i.to, i]));
    return [...guardado.map((to) => porRuta.get(to)).filter(Boolean),
            ...disponibles.filter((i) => !guardado.includes(i.to))];
  };

  const mover = (g, idx, dir) => {
    const items = itemsDe(g);
    const j = idx + dir;
    if (j < 0 || j >= items.length) return;
    [items[idx], items[j]] = [items[j], items[idx]];
    const next = { ...orden, [g.titulo]: items.map((i) => i.to) };
    setOrden(next);
    localStorage.setItem("sis_nav_orden", JSON.stringify(next));
  };

  const plegar = (titulo) => {
    const next = cerrados.includes(titulo)
      ? cerrados.filter((t) => t !== titulo)
      : [...cerrados, titulo];
    setCerrados(next);
    localStorage.setItem("sis_nav_cerrados", JSON.stringify(next));
  };

  const restablecer = () => {
    localStorage.removeItem("sis_nav_orden");
    localStorage.removeItem("sis_nav_cerrados");
    setOrden({});
    setCerrados([]);
  };

  return (
    <nav className="flex flex-col gap-4" data-testid="nav-admin">
      <div className="flex items-center justify-between px-2">
        <button onClick={() => setEditando(!editando)} data-testid="organizar-menu"
                className={`flex items-center gap-2 rounded-full px-3 py-1.5 text-xs font-semibold lift ${
                  editando ? "bg-primary text-primary-foreground" : "text-muted-foreground hover:bg-muted"}`}>
          <Settings2 className="h-3.5 w-3.5" /> {editando ? "Listo" : "Organizar menú"}
        </button>
        {editando && (
          <button onClick={restablecer} title="Restablecer orden" data-testid="restablecer-menu"
                  className="rounded-full p-2 text-muted-foreground hover:bg-muted">
            <RotateCcw className="h-3.5 w-3.5" />
          </button>
        )}
      </div>

      {GRUPOS_ADMIN.map((g) => {
        const abierto = !cerrados.includes(g.titulo);
        const items = itemsDe(g);
        if (items.length === 0) return null;
        return (
          <div key={g.titulo}>
            <button onClick={() => plegar(g.titulo)} data-testid={`grupo-${g.titulo.toLowerCase()}`}
                    className="flex w-full items-center gap-2 px-4 py-1.5 text-[11px] font-bold uppercase tracking-[0.18em] text-muted-foreground hover:text-foreground">
              {abierto ? <ChevronDown className="h-3 w-3" /> : <ChevronRight className="h-3 w-3" />}
              {g.titulo}
            </button>
            {abierto && (
              <div className="mt-1 flex flex-col gap-1">
                {items.map((i, idx) => (
                  <div key={i.to} className="flex items-center gap-1">
                    <NavLink to={i.to} end={i.to === "/admin"} data-testid={i.tid}
                             onClick={onNavigate}
                             className={({ isActive }) =>
                               `flex flex-1 items-center gap-3 rounded-md px-4 py-2.5 text-sm lift ${
                                 isActive ? "bg-primary text-primary-foreground font-semibold"
                                          : "text-muted-foreground hover:bg-muted hover:text-foreground"}`}>
                      <i.icon className="h-4 w-4 shrink-0" />
                      {i.label}
                    </NavLink>
                    {editando && (
                      <span className="flex flex-col">
                        <button onClick={() => mover(g, idx, -1)} data-testid={`subir-${i.tid}`}
                                className="rounded p-0.5 text-muted-foreground hover:bg-muted">
                          <ArrowUp className="h-3 w-3" />
                        </button>
                        <button onClick={() => mover(g, idx, 1)} data-testid={`bajar-${i.tid}`}
                                className="rounded p-0.5 text-muted-foreground hover:bg-muted">
                          <ArrowDown className="h-3 w-3" />
                        </button>
                      </span>
                    )}
                  </div>
                ))}
              </div>
            )}
          </div>
        );
      })}
    </nav>
  );
};
